/**
 * DEFUSE S15 — common object-property descriptors (shared by every entity kind).
 *
 * The 10 `commonObjectProperties` (Color/Layer/Linetype/Linetype Scale/
 * Lineweight/Thickness/Transparency/Plot Style/Material/Annotative) route
 * through ONE builder so the per-kind tables carry only geometry/type-specific
 * fields. Each kind surfaces a DIFFERENT subset of the common set (AutoCAD
 * parity — e.g. MTEXT has no Linetype Scale row, Viewport has no Material) so
 * the builder emits a descriptor ONLY for a common label actually present in
 * that kind's `propertyCapabilities` roster — keeping the descriptor set exactly
 * 1:1 with the current string path.
 *
 * The label→field map mirrors `propertyTranslator.commonLabelToEntityField`
 * (apps/web) + the per-type `THICKNESS_BEARING_ENTITY_TYPES` gate — one truth,
 * read here from `drawing-model`, so S15's write cutover matches by construction.
 */

import type { EntityType } from "@dxf-ai-editor/contracts";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";
import {
  THICKNESS_BEARING_ENTITY_TYPES,
  coerceColor,
  colorToLegacyString,
  deserializeColor,
} from "@dxf-ai-editor/drawing-model";

import { formatNumber } from "../geometryEntityTypes";
import {
  commonObjectProperties,
  getEntityPropertyCapabilities,
} from "../propertyCapabilities";
import {
  calc,
  make,
  num,
  propertyMessageKey,
  type EditorKind,
  type TypedPropertyDescriptor,
} from "./types";

/** ui-projection-deferred row (no typed home, NOT writable) that still projects
 *  the AutoCAD DEFAULT the production panel shows (Plot Style "ByColor" /
 *  Material "ByLayer"); S16 overlays the handle-graph-resolved name. */
function deferredWithDefault(label: string, defaultValue: string): TypedPropertyDescriptor {
  return make(label, "text", undefined, {
    channel: "ui-projection-deferred",
    constraints: { readOnly: true },
    display: () => defaultValue,
  });
}

/** Common keys `dxfGeometry.commonProperties` can surface on ANY kind, so a
 *  descriptor must always exist even when a kind's narrower CHPROP roster omits
 *  them. The first 6 are ALWAYS in the bag; `Linetype Scale` (DXF group 48) is
 *  emitted for ANY entity carrying the optional field regardless of kind — so an
 *  imported MText/Hatch/… with group 48 still gets a (read-only) row rather than
 *  losing it (P2 R6 #1). Per-kind writability is governed separately. */
const ALWAYS_COMMON = new Set<string>([
  "Layer",
  "Color",
  "Linetype",
  "Lineweight",
  "Thickness",
  "Transparency",
  "Linetype Scale",
]);

/** every place `propertyCapabilities` can surface a property for a kind, plus
 *  the always-emitted common bag keys. */
function commonRosterFor(kind: EntityType): ReadonlySet<string> {
  const caps = getEntityPropertyCapabilities(kind);
  return new Set<string>([
    ...ALWAYS_COMMON,
    ...caps.editableInProperties,
    ...caps.editableInChprop,
    ...caps.copyableInMatchpropBasic,
    ...caps.copyableInMatchpropSpecial,
  ]);
}

/** annotative flag is derived — an entity is annotative iff it carries ≥1
 *  per-scale context snapshot (AutoCAD parity; see multileaderEntitySchema note). */
function isAnnotative(entity: DrawingEntity): boolean {
  const contexts = (entity as { annotativeContexts?: unknown[] }).annotativeContexts;
  return Array.isArray(contexts) && contexts.length > 0;
}

function annotativeContextsOf(
  entity: DrawingEntity,
): ReadonlyArray<{ scaleName?: string }> {
  const contexts = (entity as { annotativeContexts?: { scaleName?: string }[] })
    .annotativeContexts;
  return Array.isArray(contexts) ? contexts : [];
}

/** base-schema string field (Color union / Layer / Linetype / Lineweight /
 *  Transparency) — one truth for the message-key + typed accessor plumbing.
 *  `editable=false` keeps the row SHOWN (read+display) but read-only, so S15
 *  never patches a common prop a kind's CHPROP gate excludes (P2 R5 #3). */
function baseStr(
  label: string,
  editorKind: EditorKind,
  path: string,
  read: (e: DrawingEntity) => unknown,
  editable: boolean,
): TypedPropertyDescriptor {
  const base = {
    label,
    messageKey: propertyMessageKey(label),
    editorKind,
    typedFieldRef: { path, read },
  } as const;
  return editable
    ? { ...base, channel: "editable-typed-field" }
    : { ...base, channel: "read-only-calc", constraints: { readOnly: true } };
}

/** the common props a kind's production path actually PATCHES (properties panel
 *  + CHPROP write channels) — mirrors `canApplyProperty` / `propertyTranslator`
 *  gates. `commonProperties` DISPLAYS all 6 always-common keys on every kind, but
 *  writability is per-kind (e.g. MText/Hatch/Viewport exclude Linetype). */
function editableCommonRosterFor(kind: EntityType): ReadonlySet<string> {
  const caps = getEntityPropertyCapabilities(kind);
  return new Set<string>([...caps.editableInProperties, ...caps.editableInChprop]);
}

/** Build the common-object descriptors present in `kind`'s current roster. */
export function buildCommonDescriptors(kind: EntityType): TypedPropertyDescriptor[] {
  const roster = commonRosterFor(kind);
  const editable = editableCommonRosterFor(kind);
  const thicknessBearing = THICKNESS_BEARING_ENTITY_TYPES.has(kind);
  const out: TypedPropertyDescriptor[] = [];

  for (const label of commonObjectProperties) {
    if (!roster.has(label)) continue;
    switch (label) {
      case "Color":
        // typed `EntityColor` union on the base schema (EPIC-COLOR-UNION S1). The
        // panel projects it to the legacy string (ByLayer / ACI / #rrggbb) via
        // the ONE-TRUTH `colorToLegacyString(coerceColor(...))` the production
        // preview uses (dxfGeometry.commonProperties) — read=raw union (S15
        // home), display=projected string.
        //
        // S15 Color cutover (write route): toRaw = `deserializeColor`, NOT the
        // lossy `coerceColor`. The typed Color writer (`buildTypedColorEdit`) now
        // owns the live Color edit, and a deliberate write can carry the lossless
        // `!EC:{…}` rich payload (a MATCHPROP copy of a true-colour-with-ACI /
        // color-book source, the undo inverse, an explode commit). `coerceColor`
        // DROPS that payload to `{kind:byLayer}` (it is neither a legacy string it
        // recognises nor a typed object) → data-loss on every rich colour;
        // `deserializeColor` decodes the `!EC:` form back to the full union while
        // still routing a plain legacy string ("5"/"ByLayer"/"#rrggbb") through
        // `coerceColor` (it is a strict superset). display stays lossy on purpose
        // (the panel shows the legacy string) — the write inverse must be lossless.
        out.push(
          make("Color", "color", { path: "color", read: (e) => e.color }, {
            display: (e) => colorToLegacyString(coerceColor((e as DrawingEntity).color)),
            toRaw: (s) => deserializeColor(s),
          }),
        );
        break;
      case "Layer":
        out.push(baseStr("Layer", "layer-ref", "layer", (e) => e.layer, editable.has("Layer")));
        break;
      case "Linetype":
        // per-kind gate: MText/Hatch/Multileader/Revcloud/Viewport SHOW Linetype
        // (commonProperties "ByLayer") but CHPROP excludes it → read-only there.
        out.push(
          baseStr("Linetype", "linetype-ref", "linetype", (e) => e.linetype, editable.has("Linetype")),
        );
        break;
      case "Linetype Scale":
        // DXF group 48 — OPTIONAL. The production preview OMITS the row when
        // absent (default 1); read=effective (?? 1), display="" when omitted so
        // S16 hides it (P2 #2). Per-kind editable gate (P2 R5 #3) — writable only
        // where CHPROP patches it.
        out.push(
          make(
            "Linetype Scale",
            "number",
            { path: "linetypeScale", read: (e) => (e as DrawingEntity).linetypeScale ?? 1 },
            {
              channel: editable.has("Linetype Scale") ? "editable-typed-field" : "read-only-calc",
              constraints: editable.has("Linetype Scale")
                ? { numeric: { precision: 3 } }
                : { numeric: { precision: 3 }, readOnly: true },
              display: (e) =>
                (e as DrawingEntity).linetypeScale === undefined
                  ? ""
                  : formatNumber((e as DrawingEntity).linetypeScale as number),
              ...(editable.has("Linetype Scale") ? { toRaw: (s: string) => Number(s) } : {}),
            },
          ),
        );
        break;
      case "Lineweight":
        out.push(
          baseStr("Lineweight", "lineweight", "lineweight", (e) => e.lineweight, editable.has("Lineweight")),
        );
        break;
      case "Transparency":
        out.push(
          baseStr("Transparency", "text", "transparency", (e) => e.transparency, editable.has("Transparency")),
        );
        break;
      case "Thickness":
        // `commonProperties` emits Thickness "0" for EVERY kind. Editable ONLY on
        // a thickness-BEARING kind whose CHPROP patches it (P2 R5 #3):
        //   • bearing + patchable      → editable num;
        //   • bearing + CHPROP-excluded (Dimension/Revcloud) → read-only value;
        //   • non-bearing              → ui-projection "0" (no field).
        // S15 P3-2 — the `?? 0` read makes an UNDEFINED thickness display "0"; a
        // re-submit of "0" therefore NO-OPS (the S15 writer's display-equivalence)
        // and PRESERVES the absent field, rather than MATERIALIZING `thickness: 0`
        // the way legacy `mergeCommonFieldsForType` (`Number("0")`) did. This is an
        // INTENDED, export-BYTE-IDENTICAL difference: the DXF writer omits group 39
        // for undefined AND 0 alike (dxfBridge.drawingToDxf.ts:524 omits it
        // explicitly for the Dimension arm; every other arm normalises via
        // `thickness ?? 0`), so no DXF / render regression — only the transient
        // typed field's absent-vs-0 shape differs, which nothing reads.
        out.push(
          thicknessBearing && editable.has("Thickness")
            ? num<DrawingEntity>("Thickness", "thickness", (e) =>
                (e as { thickness?: number }).thickness ?? 0,
              )
            : thicknessBearing
              ? make("Thickness", "number", {
                  path: "thickness",
                  read: (e) => (e as { thickness?: number }).thickness ?? 0,
                }, {
                  channel: "read-only-calc",
                  constraints: { numeric: { precision: 3 }, readOnly: true },
                })
              : make("Thickness", "number", undefined, {
                  channel: "ui-projection-deferred",
                  constraints: { readOnly: true },
                  display: () => "0",
                }),
        );
        break;
      case "Plot Style":
        // no typed home for the display VALUE (only the opaque 390 handle) —
        // ui-projection-deferred (NOT writable, NOT deletable). The panel shows
        // the AutoCAD default "ByColor" (the preview deriver's `?? "ByColor"`);
        // S16 resolves the plot-style NAME from the handle graph (P2 #1).
        out.push(deferredWithDefault("Plot Style", "ByColor"));
        break;
      case "Material":
        out.push(deferredWithDefault("Material", "ByLayer"));
        break;
      case "Annotative":
        // The annotative projection is THREE palette rows (deriveAnnotativeRows):
        // Annotative (Yes/No), Annotation scale, Annotative scales (P2 #13).
        // Derived read-only-calc; the underlying edit is the ANNOTATIVE command /
        // annotativeContexts array (side-channel today, §3.1).
        out.push(
          calc<DrawingEntity>("Annotative", isAnnotative, (e) =>
            isAnnotative(e) ? "Yes" : "No",
          ),
          calc<DrawingEntity>(
            "Annotation scale",
            (e) => (e as { creationScaleName?: string }).creationScaleName ?? "—",
          ),
          calc<DrawingEntity>("Annotative scales", (e) =>
            annotativeContextsOf(e)
              .map((c) => c.scaleName)
              .filter((n): n is string => typeof n === "string" && n.length > 0)
              .join(", "),
          ),
        );
        break;
      default:
        break;
    }
  }
  return out;
}
